import { computed, ComputedRef, Ref } from "vue";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { EventParticipant } from "@models/EventParticipant";
import { ParticipantStop } from "@models/ParticipantStop";
import { Stop } from "@models/Stop";
import Constants from "@/models/Constants";

/**
 * Composable for working with stops in a poker run event
 * @param event Ref to a PokerRunEvent
 * @param participant Optional Ref to an EventParticipant
 */
export default function useStops(
  event: Ref<PokerRunEvent | undefined>,
  participant: Ref<EventParticipant>
) {
  /**
   * Get the last checked-in stop for the participant
   */
  const lastCheckedInStop = computed<ParticipantStop | null>(() => {
    if (!participant?.value || !participant.value.stops || participant.value.stops.length === 0) {
      return null;
    }

    const checkedInStops = participant.value.stops.filter((stop) => stop.checkedIn);
    return checkedInStops.length > 0 ? checkedInStops[checkedInStops.length - 1] : null;
  });

  /**
   * Check if there is a next stop available for the participant
   */
  const hasNextStop = computed<boolean>(() => {
    if (!participant?.value || !participant.value.stops) {
      return false;
    }
    return participant.value.stops.some((stop) => !stop.checkedIn);
  });

  /**
   * Get the next stop for the event based on the last checked-in stop
   */
  const nextStop = computed<Stop | null>(() => {
    if (!event.value || !event.value.stops || event.value.stops.length === 0) {
      return firstStop.value;
    }

    if (lastCheckedInStop.value !== null) {
      return (
        (event.value.stops.find(
          (stop) => stop.order === (lastCheckedInStop.value?.order ?? 0) + 1
        ) as Stop) || firstStop.value
      );
    }

    return event.value.stops[0];
  });

  const firstStop = computed<Stop>(() => {
    if (!event.value || !event.value.stops || event.value.stops.length === 0) {
      return {} as Stop;
    }

    return event.value.stops[0];
  });

  const orderedStops = computed<Stop[]>(() => {
    if (!event.value || !event.value.stops || event.value.stops.length === 0) {
      return [];
    }

    return event.value.stops.sort((a, b) => {
      return a.order - b.order;
    });
  });

  /**
   * Get the first missed stop for the participant
   */
  const firstMissedStop = computed<Stop | null>(() => {
    if (
      !event.value ||
      !participant?.value ||
      !participant.value.stops ||
      participant.value.stops.length === 0
    ) {
      return null;
    }

    // No missed stops in scavenger hunt mode
    if (event.value.eventSettings?.cardReleaseStyle === Constants.CARD_RELEASE_STYLE_SCAVENGER) {
      return null;
    }

    const maxCheckedInStop = participant.value.stops
      .filter((stop) => stop.checkedIn)
      .reduce((prev, current) => (prev.order > current.order ? prev : current), {
        order: 0,
      } as ParticipantStop);

    const minNotCheckedInStop = participant.value.stops
      .filter((stop) => !stop.checkedIn)
      .reduce((prev, current) => (prev.order < current.order ? prev : current), {
        order: 1000,
      } as ParticipantStop);

    if (maxCheckedInStop.order === 0 || minNotCheckedInStop.order === 1000) {
      return null;
    }

    const missedStop = event.value.stops.find((stop) => stop.order === minNotCheckedInStop.order);

    return maxCheckedInStop.order > minNotCheckedInStop.order ? missedStop ?? null : null;
  });

  const numberOfCheckedInStops = computed<number>(() => {
    if (!participant?.value || !participant.value.stops) {
      return 0;
    }
    return participant.value.stops.filter((stop) => stop.checkedIn).length;
  });

  /**
   * Get the stop name by order
   */
  const getStopName = (stopOrder: number): string | undefined => {
    if (!event.value || !event.value.stops) {
      return undefined;
    }

    const stop = event.value.stops.find((s) => s.order === stopOrder);
    return stop?.name;
  };

  /**
   * Check if a stop is checked in
   */
  const getStopCheckedInStatus = (
    stopOrder: number
  ): ComputedRef<"checkedIn" | "notCheckedIn" | "missedCheckIn" | "skippedCheckin"> => {
    return computed(() => {
      if (!participant?.value || !participant.value.stops || event.value === undefined) {
        return "notCheckedIn";
      }

      const doesNotAllowSkipping = event.value.eventSettings.cardReleaseStyle === "strict";
      const currentStopCheckedIn = participant.value.stops.find(
        (stop) => stop.order === stopOrder && stop.checkedIn
      );
      const futureStopCheckedIn = participant.value.stops.find(
        (stop) => stop.order > stopOrder && stop.checkedIn
      );
      let status = "notCheckedIn";
      if (currentStopCheckedIn) {
        status = "checkedIn";
      } else if (doesNotAllowSkipping && futureStopCheckedIn) {
        status = "skippedCheckin";
      } else if (futureStopCheckedIn) {
        status = "missedCheckIn";
      }
      console.log("getStopCheckedInStatus", stopOrder, status);
      return status as "checkedIn" | "notCheckedIn" | "missedCheckIn" | "skippedCheckin";
    });
  };

  /**
   * Get the next stop time
   */
  const nextStopTime = computed<string | null>(() => {
    if (!nextStop.value || !nextStop.value.expectedArrivalTime) {
      return null;
    }

    return nextStop.value.expectedArrivalTime as string;
  });

  return {
    lastCheckedInStop,
    hasNextStop,
    nextStop,
    firstStop,
    firstMissedStop,
    getStopName,
    orderedStops,
    getStopCheckedInStatus,
    nextStopTime,
  };
}
